import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image as ImageIcon, Loader2, Sparkles, Type, X } from 'lucide-react';

import { Button } from '@/components/common/Button';
import { useLivelloIndietro } from '@/hooks/useLivelloIndietro';
import { useHaptics } from '@/hooks/useHaptics';
import { cn } from '@/lib/cn';
import { PromptInIngleseHint } from '@/components/avatars/PromptInIngleseHint';

type Tab = 'text' | 'multi-image';
type Status = 'idle' | 'pending';

interface Props {
  open: boolean;
  isPending?: boolean;
  onClose: () => void;
  /**
   * Called when user hits Generate. Caller dispatches the right mutation
   * (text-to-3d OR multi-image-to-3d) and routes to AvatarDetail on success.
   */
  onGenerateText: (textPrompt: string, title?: string) => Promise<void>;
  onGenerateMultiImage: (files: File[], title?: string) => Promise<void>;
}

const MAX_PROMPT_CHARS = 600;

/**
 * Phase 5+ — Direct 3D creation modal. Two tabs:
 *   - "From text"        → Meshy text-to-3d  ($0.40)
 *   - "From photos"      → Meshy multi-image-to-3d (1-4 photos, $0.60)
 *
 * Skips the Gemini 2D step entirely.
 */
export function Direct3dModal({ open, isPending, onClose, onGenerateText, onGenerateMultiImage }: Props) {
  const { t } = useTranslation('avatars');
  useLivelloIndietro(open, onClose);
  const haptics = useHaptics();
  const [tab, setTab] = useState<Tab>('text');
  const [prompt, setPrompt] = useState('');
  const [title, setTitle] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [status, setStatus] = useState<Status>('idle');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  if (!open) return null;

  const trimmedPrompt = prompt.trim();
  const canSubmitText = trimmedPrompt.length >= 3 && trimmedPrompt.length <= MAX_PROMPT_CHARS && !isPending && status === 'idle';
  const canSubmitMulti = files.length >= 1 && files.length <= 4 && !isPending && status === 'idle';

  const handleGenerate = async () => {
    haptics.impact('medium');
    setStatus('pending');
    try {
      if (tab === 'text' && canSubmitText) {
        await onGenerateText(trimmedPrompt, title.trim() || undefined);
      } else if (tab === 'multi-image' && canSubmitMulti) {
        await onGenerateMultiImage(files, title.trim() || undefined);
      }
    } finally {
      setStatus('idle');
    }
  };

  const onPickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = Array.from(e.target.files ?? []).slice(0, 4);
    setFiles(next);
    haptics.selection();
  };

  const removeFile = (i: number) => {
    setFiles(prev => prev.filter((_, idx) => idx !== i));
    haptics.selection();
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-bg/95 backdrop-blur">
      <header className="flex items-center justify-between px-4 py-4">
        <div>
          <h1 className="text-lg font-bold text-fg">{t('direct3d.title')}</h1>
          <p className="mt-0.5 text-xs text-fg-muted">{t('direct3d.subtitle')}</p>
        </div>
        <Button
          variant="icona"
          size={36}
          tono="pieno"
          aria-label={t('common.close')}
          onClick={() => { haptics.impact('light'); onClose(); }}
        >
          <X className="h-5 w-5" />
        </Button>
      </header>

      {/* Tab strip */}
      <div className="mx-4 mb-3 flex gap-1 rounded-full bg-bg-secondary p-1">
        {(['text', 'multi-image'] as Tab[]).map(tabId => {
          const active = tab === tabId;
          return (
            <button
              key={tabId}
              type="button"
              onClick={() => { haptics.selection(); setTab(tabId); }}
              className={cn(
                'flex flex-1 items-center justify-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold transition-colors',
                active ? 'bg-brand text-brand-fg' : 'text-fg-muted',
              )}
            >
              {tabId === 'text' ? <Type aria-hidden className="h-3.5 w-3.5" /> : <ImageIcon aria-hidden className="h-3.5 w-3.5" />}
              {tabId === 'text' ? t('direct3d.tabText') : t('direct3d.tabPhotos')}
            </button>
          );
        })}
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-32">
        <div className="flex flex-col gap-3">
          {/* Common title field */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase text-fg-muted" htmlFor="d3d-title">{t('direct3d.titleLabel')}</label>
            <input
              id="d3d-title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              maxLength={64}
              placeholder={t('direct3d.titlePlaceholder')}
              className="rounded-xl bg-bg-secondary px-3 py-2 text-sm text-fg placeholder:text-fg-muted/60 focus:outline-none"
            />
          </div>

          {tab === 'text' ? (
            <>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold uppercase text-fg-muted" htmlFor="d3d-prompt">{t('direct3d.promptLabel')}</label>
                <textarea
                  id="d3d-prompt"
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  maxLength={MAX_PROMPT_CHARS}
                  placeholder={t('direct3d.promptPlaceholder')}
                  className="min-h-[140px] rounded-xl bg-bg-secondary p-3 text-sm text-fg placeholder:text-fg-muted/60 focus:outline-none"
                />
                <PromptInIngleseHint />
                <p className="text-right text-[11px] text-fg-muted">{trimmedPrompt.length} / {MAX_PROMPT_CHARS}</p>
              </div>
              <div className="rounded-xl bg-bg-secondary p-3 text-xs leading-relaxed text-fg-muted">
                <p className="font-semibold text-fg">{t('direct3d.quickTitle')}</p>
                <p className="mt-1">{t('direct3d.quickBody')}</p>
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold uppercase text-fg-muted">{t('direct3d.photosLabel')}</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={onPickFiles}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-fg-muted/40 bg-bg-secondary px-3 py-3 text-sm text-fg-muted"
                >
                  <ImageIcon aria-hidden className="h-4 w-4" />
                  {files.length > 0 ? t('direct3d.photosSelected', { n: files.length }) : t('direct3d.photosPick')}
                </button>
                {files.length > 0 && (
                  <ul className="flex flex-col gap-1">
                    {files.map((f, i) => (
                      <li key={`${f.name}-${i}`} className="flex items-center justify-between gap-2 rounded-lg bg-bg-secondary px-3 py-1.5 text-xs">
                        <span className="line-clamp-1 text-fg">{f.name}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(i)}
                          aria-label={t('common.remove')}
                          className="rounded-full p-1 text-fg-muted active:opacity-80"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="rounded-xl bg-bg-secondary p-3 text-xs leading-relaxed text-fg-muted">
                <p className="font-semibold text-fg">{t('direct3d.deluxeTitle')}</p>
                <p className="mt-1">{t('direct3d.deluxeBody')}</p>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0 border-t border-border bg-bg/95 px-4 py-3 backdrop-blur">
        <Button
          fullWidth
          onClick={handleGenerate}
          disabled={tab === 'text' ? !canSubmitText : !canSubmitMulti}
        >
          {status === 'pending' || isPending ? (
            <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles aria-hidden className="h-4 w-4" />
          )}
          {t('direct3d.generate', { cost: tab === 'text' ? '0.40' : '0.60' })}
        </Button>
      </div>
    </div>
  );
}
